/**
 * Controlled vocabularies for Melio's intake pipeline.
 *
 * This file is the single source of truth for the codes the decision engine
 * expects (conditions, relations, pattern flags, workflow states). Both the
 * doctor portal and ops portal import from identical copies of this file so
 * that patient-entered data, clinician-edited data, and engine-consumed data
 * speak the same language.
 *
 * Spec references:
 *   - docs/markdown/clinical/MELIO_QUARTERLY_REPORT_DECISION_ENGINE_V1.md §4.2, §4.3, §5.10, §7
 *   - docs/markdown/clinical/CLINICIAN_FAMILY_HISTORY_INTAKE_SPEC.md
 */

/* =====================================================================
 * Workflow status — two-tier care-team model
 * ===================================================================== */

/**
 * Canonical workflow states. `care_clinician_review` and
 * `medical_director_signoff` replace the legacy `doctor_in_review` /
 * `ops_signoff_pending` labels but we keep the legacy values in the union
 * for backward read compatibility with existing Firestore documents.
 *
 * `awaiting_senior_assignment` sits between them. A Care Clinician finishes the
 * interview and hands it on, but does NOT choose who signs it off — the
 * Relationship Manager does that later, as a separate decision. Without an
 * explicit state for that gap the intake would sit at
 * `medical_director_signoff` with nobody assigned, which is INVISIBLE to the
 * doctors sign-off queue: that queue filters on assignedMedicalDirectorName.
 * The status is what makes the wait a queue rather than a silence.
 */
export type IntakeWorkflowStatus =
  | 'draft'
  | 'submitted'
  | 'changes_requested'
  | 'care_clinician_review'
  | 'awaiting_senior_assignment'
  | 'medical_director_signoff'
  | 'released'
  | 'doctor_in_review'
  | 'ops_signoff_pending'
  | 'signed_off';

export const INTAKE_STATUS_LABELS: Record<IntakeWorkflowStatus, string> = {
  draft: 'Patient drafting',
  submitted: 'Submitted — awaiting triage',
  changes_requested: 'Changes requested',
  care_clinician_review: 'Clinical review',
  awaiting_senior_assignment: 'Awaiting senior assignment',
  medical_director_signoff: 'Medical-director sign-off',
  released: 'Released',
  doctor_in_review: 'Clinical review (legacy label)',
  ops_signoff_pending: 'Medical-director sign-off (legacy label)',
  signed_off: 'Released (legacy label)',
};

/** Normalises legacy status words to the new two-tier vocabulary. */
export function normalizeIntakeStatus(status: string | null | undefined): IntakeWorkflowStatus {
  switch (status) {
    case 'doctor_in_review':
      return 'care_clinician_review';
    case 'ops_signoff_pending':
      return 'medical_director_signoff';
    case 'signed_off':
      return 'released';
    case 'draft':
    case 'submitted':
    case 'changes_requested':
    case 'care_clinician_review':
    case 'awaiting_senior_assignment':
    case 'medical_director_signoff':
    case 'released':
      return status as IntakeWorkflowStatus;
    default:
      return 'draft';
  }
}

/** Statuses that mean "a care-clinician should look at this". */
export const CARE_CLINICIAN_OPEN_STATUSES: IntakeWorkflowStatus[] = [
  'submitted',
  'care_clinician_review',
  'doctor_in_review',
];

/** Statuses that mean "a medical director should sign this off". */
export const MEDICAL_DIRECTOR_OPEN_STATUSES: IntakeWorkflowStatus[] = [
  'medical_director_signoff',
  'ops_signoff_pending',
];

/**
 * Statuses that mean "a Relationship Manager needs to assign a senior".
 *
 * Its own list rather than a member of the two above, because the work is
 * neither clinical review nor sign-off — it is choosing who does the sign-off,
 * and it belongs to a coordinator.
 */
export const SENIOR_ASSIGNMENT_OPEN_STATUSES: IntakeWorkflowStatus[] = [
  'awaiting_senior_assignment',
];

/* =====================================================================
 * Relation codes — lineage-encoded
 * ===================================================================== */

export type IntakeLineageSide = 'maternal' | 'paternal' | 'both' | 'not_applicable' | 'unknown';

/**
 * Expanded relation codes with lineage encoded at the code level.
 * The decision engine uses these to derive first-degree vs second-degree
 * counts and to split "maternal side" vs "paternal side" pattern searches.
 */
export type IntakeRelation =
  | 'self'
  | 'mother'
  | 'father'
  | 'sister'
  | 'brother'
  | 'half_sister'
  | 'half_brother'
  | 'daughter'
  | 'son'
  | 'maternal_grandmother'
  | 'maternal_grandfather'
  | 'paternal_grandmother'
  | 'paternal_grandfather'
  | 'maternal_aunt'
  | 'maternal_uncle'
  | 'paternal_aunt'
  | 'paternal_uncle'
  | 'maternal_cousin'
  | 'paternal_cousin'
  // Legacy coarse values — still accepted on read, mapped via `normalizeRelation`.
  | 'grandmother'
  | 'grandfather'
  | 'aunt'
  | 'uncle'
  | 'cousin'
  | 'child'
  | 'other';

export interface RelationMeta {
  code: IntakeRelation;
  label: string;
  lineage: IntakeLineageSide;
  degree: 1 | 2 | 3;
  /** Canonical sort order (closest first). */
  sortOrder: number;
  legacy?: boolean;
}

export const RELATION_CATALOG: Record<IntakeRelation, RelationMeta> = {
  self: { code: 'self', label: 'Self', lineage: 'not_applicable', degree: 1, sortOrder: 0 },
  mother: { code: 'mother', label: 'Mother', lineage: 'maternal', degree: 1, sortOrder: 10 },
  father: { code: 'father', label: 'Father', lineage: 'paternal', degree: 1, sortOrder: 11 },
  sister: { code: 'sister', label: 'Sister', lineage: 'both', degree: 1, sortOrder: 20 },
  brother: { code: 'brother', label: 'Brother', lineage: 'both', degree: 1, sortOrder: 21 },
  half_sister: { code: 'half_sister', label: 'Half-sister', lineage: 'unknown', degree: 2, sortOrder: 22 },
  half_brother: { code: 'half_brother', label: 'Half-brother', lineage: 'unknown', degree: 2, sortOrder: 23 },
  daughter: { code: 'daughter', label: 'Daughter', lineage: 'not_applicable', degree: 1, sortOrder: 30 },
  son: { code: 'son', label: 'Son', lineage: 'not_applicable', degree: 1, sortOrder: 31 },
  maternal_grandmother: { code: 'maternal_grandmother', label: 'Maternal grandmother', lineage: 'maternal', degree: 2, sortOrder: 40 },
  maternal_grandfather: { code: 'maternal_grandfather', label: 'Maternal grandfather', lineage: 'maternal', degree: 2, sortOrder: 41 },
  paternal_grandmother: { code: 'paternal_grandmother', label: 'Paternal grandmother', lineage: 'paternal', degree: 2, sortOrder: 42 },
  paternal_grandfather: { code: 'paternal_grandfather', label: 'Paternal grandfather', lineage: 'paternal', degree: 2, sortOrder: 43 },
  maternal_aunt: { code: 'maternal_aunt', label: 'Maternal aunt', lineage: 'maternal', degree: 2, sortOrder: 50 },
  maternal_uncle: { code: 'maternal_uncle', label: 'Maternal uncle', lineage: 'maternal', degree: 2, sortOrder: 51 },
  paternal_aunt: { code: 'paternal_aunt', label: 'Paternal aunt', lineage: 'paternal', degree: 2, sortOrder: 52 },
  paternal_uncle: { code: 'paternal_uncle', label: 'Paternal uncle', lineage: 'paternal', degree: 2, sortOrder: 53 },
  maternal_cousin: { code: 'maternal_cousin', label: 'Maternal cousin', lineage: 'maternal', degree: 3, sortOrder: 60 },
  paternal_cousin: { code: 'paternal_cousin', label: 'Paternal cousin', lineage: 'paternal', degree: 3, sortOrder: 61 },
  // Legacy coarse values — kept for read back-compat.
  grandmother: { code: 'grandmother', label: 'Grandmother (lineage unspecified)', lineage: 'unknown', degree: 2, sortOrder: 44, legacy: true },
  grandfather: { code: 'grandfather', label: 'Grandfather (lineage unspecified)', lineage: 'unknown', degree: 2, sortOrder: 45, legacy: true },
  aunt: { code: 'aunt', label: 'Aunt (lineage unspecified)', lineage: 'unknown', degree: 2, sortOrder: 54, legacy: true },
  uncle: { code: 'uncle', label: 'Uncle (lineage unspecified)', lineage: 'unknown', degree: 2, sortOrder: 55, legacy: true },
  cousin: { code: 'cousin', label: 'Cousin (lineage unspecified)', lineage: 'unknown', degree: 3, sortOrder: 62, legacy: true },
  child: { code: 'child', label: 'Child (sex unspecified)', lineage: 'not_applicable', degree: 1, sortOrder: 32, legacy: true },
  other: { code: 'other', label: 'Other / unspecified', lineage: 'unknown', degree: 3, sortOrder: 99 },
};

/** Primary (non-legacy) relation codes for new UIs. */
export const RELATION_CODES: IntakeRelation[] = Object.values(RELATION_CATALOG)
  .filter((meta) => !meta.legacy && meta.code !== 'self')
  .sort((a, b) => a.sortOrder - b.sortOrder)
  .map((meta) => meta.code);

/** Relation codes grouped by degree — useful for the readiness checklist. */
export const RELATIONS_BY_DEGREE: Record<1 | 2 | 3, IntakeRelation[]> = {
  1: RELATION_CODES.filter((code) => RELATION_CATALOG[code].degree === 1),
  2: RELATION_CODES.filter((code) => RELATION_CATALOG[code].degree === 2),
  3: RELATION_CODES.filter((code) => RELATION_CATALOG[code].degree === 3),
};

export function normalizeRelation(raw: string | null | undefined): IntakeRelation {
  if (!raw) return 'other';
  if ((RELATION_CATALOG as Record<string, RelationMeta>)[raw]) return raw as IntakeRelation;
  return 'other';
}

/* =====================================================================
 * Condition codes — aligned with Decision Engine V1 §4.2
 * ===================================================================== */

export type ConditionCategory =
  | 'cardiovascular'
  | 'cancer'
  | 'metabolic'
  | 'neurological'
  | 'thrombosis'
  | 'mental_health'
  | 'autoimmune'
  | 'other';

export type IntakeConditionCode =
  // Cardiovascular
  | 'CAD'
  | 'MI'
  | 'STROKE'
  | 'SUDDEN_CARDIAC_DEATH'
  | 'ARRHYTHMIA'
  // Cancer
  | 'BREAST'
  | 'OVARIAN'
  | 'COLORECTAL'
  | 'PROSTATE'
  | 'PANCREATIC'
  | 'MELANOMA'
  | 'ENDOMETRIAL'
  | 'OTHER_CANCER'
  // Metabolic
  | 'T2D'
  | 'T1D'
  | 'OBESITY'
  | 'HYPERTENSION'
  | 'HYPERLIPIDAEMIA'
  // Neurological
  | 'DEMENTIA'
  | 'DEMENTIA_EARLY'
  | 'PARKINSON'
  | 'MOTOR_NEURON'
  // Thrombosis
  | 'VTE'
  | 'PE'
  | 'DVT'
  // Mental health
  | 'DEPRESSION'
  | 'BIPOLAR'
  | 'SCHIZOPHRENIA'
  // Autoimmune
  | 'RA'
  | 'LUPUS'
  | 'COELIAC'
  // Fallback
  | 'OTHER';

export interface ConditionMeta {
  code: IntakeConditionCode;
  label: string;
  category: ConditionCategory;
  /** Whether the decision engine has a V1 rule that uses this code. */
  usedInV1Rules: boolean;
  /** Short help text shown to care clinicians when they pick this code. */
  hint?: string;
}

export const CONDITION_CATALOG: Record<IntakeConditionCode, ConditionMeta> = {
  CAD: { code: 'CAD', label: 'Coronary artery disease', category: 'cardiovascular', usedInV1Rules: true, hint: 'Includes angina, bypass, stent, or documented CAD.' },
  MI: { code: 'MI', label: 'Heart attack (MI)', category: 'cardiovascular', usedInV1Rules: true, hint: 'Record age at first event if known.' },
  STROKE: { code: 'STROKE', label: 'Stroke', category: 'cardiovascular', usedInV1Rules: true },
  SUDDEN_CARDIAC_DEATH: { code: 'SUDDEN_CARDIAC_DEATH', label: 'Sudden cardiac death', category: 'cardiovascular', usedInV1Rules: true, hint: 'Unexplained sudden death in someone apparently well.' },
  ARRHYTHMIA: { code: 'ARRHYTHMIA', label: 'Arrhythmia', category: 'cardiovascular', usedInV1Rules: false },

  BREAST: { code: 'BREAST', label: 'Breast cancer', category: 'cancer', usedInV1Rules: true },
  OVARIAN: { code: 'OVARIAN', label: 'Ovarian cancer', category: 'cancer', usedInV1Rules: true },
  COLORECTAL: { code: 'COLORECTAL', label: 'Colorectal cancer', category: 'cancer', usedInV1Rules: true },
  PROSTATE: { code: 'PROSTATE', label: 'Prostate cancer', category: 'cancer', usedInV1Rules: true },
  PANCREATIC: { code: 'PANCREATIC', label: 'Pancreatic cancer', category: 'cancer', usedInV1Rules: true },
  MELANOMA: { code: 'MELANOMA', label: 'Melanoma', category: 'cancer', usedInV1Rules: true },
  ENDOMETRIAL: { code: 'ENDOMETRIAL', label: 'Endometrial (uterine) cancer', category: 'cancer', usedInV1Rules: true },
  OTHER_CANCER: { code: 'OTHER_CANCER', label: 'Other cancer', category: 'cancer', usedInV1Rules: false, hint: 'Note the site in the free-text field.' },

  T2D: { code: 'T2D', label: 'Type 2 diabetes', category: 'metabolic', usedInV1Rules: true },
  T1D: { code: 'T1D', label: 'Type 1 diabetes', category: 'metabolic', usedInV1Rules: false },
  OBESITY: { code: 'OBESITY', label: 'Obesity', category: 'metabolic', usedInV1Rules: false },
  HYPERTENSION: { code: 'HYPERTENSION', label: 'Hypertension', category: 'metabolic', usedInV1Rules: true },
  HYPERLIPIDAEMIA: { code: 'HYPERLIPIDAEMIA', label: 'High cholesterol', category: 'metabolic', usedInV1Rules: true, hint: 'Flag familial hypercholesterolaemia in notes if suspected.' },

  DEMENTIA: { code: 'DEMENTIA', label: 'Dementia (Alzheimer\u2019s or other)', category: 'neurological', usedInV1Rules: true },
  DEMENTIA_EARLY: { code: 'DEMENTIA_EARLY', label: 'Early-onset dementia (<65)', category: 'neurological', usedInV1Rules: true },
  PARKINSON: { code: 'PARKINSON', label: 'Parkinson\u2019s disease', category: 'neurological', usedInV1Rules: false },
  MOTOR_NEURON: { code: 'MOTOR_NEURON', label: 'Motor neuron disease (ALS)', category: 'neurological', usedInV1Rules: false },

  VTE: { code: 'VTE', label: 'Venous thromboembolism (any)', category: 'thrombosis', usedInV1Rules: true },
  PE: { code: 'PE', label: 'Pulmonary embolism', category: 'thrombosis', usedInV1Rules: true },
  DVT: { code: 'DVT', label: 'Deep vein thrombosis', category: 'thrombosis', usedInV1Rules: true },

  DEPRESSION: { code: 'DEPRESSION', label: 'Depression', category: 'mental_health', usedInV1Rules: false },
  BIPOLAR: { code: 'BIPOLAR', label: 'Bipolar disorder', category: 'mental_health', usedInV1Rules: false },
  SCHIZOPHRENIA: { code: 'SCHIZOPHRENIA', label: 'Schizophrenia', category: 'mental_health', usedInV1Rules: false },

  RA: { code: 'RA', label: 'Rheumatoid arthritis', category: 'autoimmune', usedInV1Rules: false },
  LUPUS: { code: 'LUPUS', label: 'Lupus (SLE)', category: 'autoimmune', usedInV1Rules: false },
  COELIAC: { code: 'COELIAC', label: 'Coeliac disease', category: 'autoimmune', usedInV1Rules: false },

  OTHER: { code: 'OTHER', label: 'Other (see notes)', category: 'other', usedInV1Rules: false },
};

export const CONDITION_CODES: IntakeConditionCode[] = (
  Object.keys(CONDITION_CATALOG) as IntakeConditionCode[]
).sort((a, b) => CONDITION_CATALOG[a].label.localeCompare(CONDITION_CATALOG[b].label));

export const CONDITION_CODES_BY_CATEGORY: Record<ConditionCategory, IntakeConditionCode[]> = CONDITION_CODES.reduce(
  (acc, code) => {
    const category = CONDITION_CATALOG[code].category;
    acc[category] = acc[category] || [];
    acc[category].push(code);
    return acc;
  },
  {} as Record<ConditionCategory, IntakeConditionCode[]>,
);

/** Best-effort coerce legacy free-text labels to a code. Never throws. */
export function coerceConditionCode(raw: string | null | undefined): IntakeConditionCode | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if ((CONDITION_CATALOG as Record<string, ConditionMeta>)[trimmed]) return trimmed as IntakeConditionCode;
  const upper = trimmed.toUpperCase().replace(/[\s-]+/g, '_');
  if ((CONDITION_CATALOG as Record<string, ConditionMeta>)[upper]) return upper as IntakeConditionCode;
  const lower = trimmed.toLowerCase();
  if (/(heart attack|myocardial infarction|\bmi\b)/.test(lower)) return 'MI';
  if (/(coronary|angina|stent|bypass|cad)/.test(lower)) return 'CAD';
  if (/stroke|cva/.test(lower)) return 'STROKE';
  if (/sudden.*(death|cardiac)/.test(lower)) return 'SUDDEN_CARDIAC_DEATH';
  if (/breast/.test(lower)) return 'BREAST';
  if (/ovarian/.test(lower)) return 'OVARIAN';
  if (/colon|bowel|colorectal/.test(lower)) return 'COLORECTAL';
  if (/endometr|uterine/.test(lower)) return 'ENDOMETRIAL';
  if (/prostate/.test(lower)) return 'PROSTATE';
  if (/pancrea/.test(lower)) return 'PANCREATIC';
  if (/melanoma/.test(lower)) return 'MELANOMA';
  if (/type 2 diabet|t2dm|t2d/.test(lower)) return 'T2D';
  if (/type 1 diabet|t1dm|t1d/.test(lower)) return 'T1D';
  if (/hypertens|high blood pressure/.test(lower)) return 'HYPERTENSION';
  if (/cholesterol|hyperlipid|statin/.test(lower)) return 'HYPERLIPIDAEMIA';
  if (/dementia|alzheimer/.test(lower)) return /early|young|<\s*65/.test(lower) ? 'DEMENTIA_EARLY' : 'DEMENTIA';
  if (/parkinson/.test(lower)) return 'PARKINSON';
  if (/(als|motor neuron)/.test(lower)) return 'MOTOR_NEURON';
  if (/pulmonary embolism|\bpe\b/.test(lower)) return 'PE';
  if (/deep vein|\bdvt\b/.test(lower)) return 'DVT';
  if (/vte|thromb/.test(lower)) return 'VTE';
  if (/cancer|tumou?r|carcinoma|malignan/.test(lower)) return 'OTHER_CANCER';
  return 'OTHER';
}

/* =====================================================================
 * Pattern flags — Decision Engine V1 §4.3
 * ===================================================================== */

export type IntakePatternFlagCode =
  | 'FH.CVD.EARLY'
  | 'FH.BREAST_OVARIAN'
  | 'FH.LYNCH'
  | 'FH.MELANOMA_CLUSTER'
  | 'FH.PANCREATIC_CLUSTER'
  | 'FH.PROSTATE_AGGRESSIVE'
  | 'FH.DEMENTIA_EARLY'
  | 'FH.VTE'
  | 'FH.T2D_BURDEN'
  | 'FH.SUDDEN_DEATH'
  | 'FH.FAMILIAL_HYPERCHOLESTEROLAEMIA';

export interface PatternFlagMeta {
  code: IntakePatternFlagCode;
  label: string;
  triggerSummary: string;
  downstreamRuleRef: string;
}

export const PATTERN_FLAG_CATALOG: Record<IntakePatternFlagCode, PatternFlagMeta> = {
  'FH.CVD.EARLY': {
    code: 'FH.CVD.EARLY',
    label: 'Early cardiovascular disease',
    triggerSummary: 'CAD, MI, stroke or SCD in a first-degree relative before 55 (male) or 65 (female).',
    downstreamRuleRef: 'RULE.CVD.EARLY_FH',
  },
  'FH.BREAST_OVARIAN': {
    code: 'FH.BREAST_OVARIAN',
    label: 'Hereditary breast / ovarian pattern',
    triggerSummary: 'Breast + ovarian in same lineage, or breast <50, or \u22652 first-degree with breast cancer.',
    downstreamRuleRef: 'RULE.CANCER.BRCA_PATTERN',
  },
  'FH.LYNCH': {
    code: 'FH.LYNCH',
    label: 'Lynch-syndrome pattern',
    triggerSummary: 'Colorectal and/or endometrial cluster, any first-degree <50, or \u22653 same-lineage cases.',
    downstreamRuleRef: 'RULE.CANCER.LYNCH_PATTERN',
  },
  'FH.MELANOMA_CLUSTER': {
    code: 'FH.MELANOMA_CLUSTER',
    label: 'Melanoma cluster',
    triggerSummary: '\u22652 melanoma in first or second-degree relatives on the same lineage.',
    downstreamRuleRef: 'RULE.CANCER.MELANOMA_CLUSTER',
  },
  'FH.PANCREATIC_CLUSTER': {
    code: 'FH.PANCREATIC_CLUSTER',
    label: 'Pancreatic cluster',
    triggerSummary: '\u22652 pancreatic cancer in the same lineage, or any first-degree.',
    downstreamRuleRef: 'RULE.CANCER.PANCREATIC_CLUSTER',
  },
  'FH.PROSTATE_AGGRESSIVE': {
    code: 'FH.PROSTATE_AGGRESSIVE',
    label: 'Aggressive prostate cancer',
    triggerSummary: 'Metastatic prostate cancer, or prostate in a first-degree relative <60.',
    downstreamRuleRef: 'RULE.CANCER.PROSTATE_AGGRESSIVE',
  },
  'FH.DEMENTIA_EARLY': {
    code: 'FH.DEMENTIA_EARLY',
    label: 'Early-onset dementia',
    triggerSummary: 'Dementia in a first or second-degree relative before 65.',
    downstreamRuleRef: 'RULE.NEURO.DEMENTIA_EARLY',
  },
  'FH.VTE': {
    code: 'FH.VTE',
    label: 'VTE / thrombophilia pattern',
    triggerSummary: 'Unprovoked VTE in a first-degree relative, or any VTE <50.',
    downstreamRuleRef: 'RULE.THROMBOSIS.FH',
  },
  'FH.T2D_BURDEN': {
    code: 'FH.T2D_BURDEN',
    label: 'High type-2 diabetes burden',
    triggerSummary: '\u22652 first-degree relatives with T2D, or any first-degree diagnosed <50.',
    downstreamRuleRef: 'RULE.METABOLIC.T2D_FH',
  },
  'FH.SUDDEN_DEATH': {
    code: 'FH.SUDDEN_DEATH',
    label: 'Sudden cardiac death pattern',
    triggerSummary: 'Any first or second-degree sudden cardiac death <50.',
    downstreamRuleRef: 'RULE.CVD.SCD_FH',
  },
  'FH.FAMILIAL_HYPERCHOLESTEROLAEMIA': {
    code: 'FH.FAMILIAL_HYPERCHOLESTEROLAEMIA',
    label: 'Familial hypercholesterolaemia suspicion',
    triggerSummary: 'Personal or first-degree LDL >4.9 mmol/L, early CAD, or known FH diagnosis.',
    downstreamRuleRef: 'RULE.CVD.FH_SUSPICION',
  },
};

export const PATTERN_FLAG_CODES: IntakePatternFlagCode[] = Object.keys(
  PATTERN_FLAG_CATALOG,
) as IntakePatternFlagCode[];

/**
 * Disposition of a pattern flag after a care-clinician has reviewed it.
 * The engine only consumes flags with status `confirmed` or `suspected`.
 */
export type PatternFlagStatus = 'suspected' | 'confirmed' | 'ruled_out' | 'needs_more_info';

export interface PatternFlagAssessment {
  code: IntakePatternFlagCode;
  status: PatternFlagStatus;
  autoSuggested: boolean;
  clinicianNotes?: string;
  supportingRelativeIds?: string[];
  assessedAt?: any;
  assessedByUid?: string;
}
